import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/api/auth'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'

const schema = z.object({
  email: z.string().email('auth.errors.invalid_email'),
})

type FormData = z.infer<typeof schema>

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  const onSubmit = async (data: FormData) => {
    setIsPending(true)
    try {
      await authApi.forgotPassword(data.email)
    } catch {
      // Always show success to prevent email enumeration
    } finally {
      setIsPending(false)
      setSubmitted(true)
    }
  }

  return (
    <main className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold text-surface-900 mb-8 text-center">
          {t('auth.forgot_title')}
        </h1>

        {submitted ? (
          <Alert variant="success">{t('auth.forgot_sent')}</Alert>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
            <Input
              label={t('auth.email')}
              type="email"
              autoComplete="email"
              placeholder={t('auth.email_placeholder')}
              error={errors.email?.message && t(errors.email.message)}
              {...register('email')}
            />
            <Button type="submit" isLoading={isPending} className="w-full">
              {t('auth.forgot_submit')}
            </Button>
            <p className="text-center text-sm">
              <Link to="/auth/login" className="text-primary-600 hover:underline">
                {t('auth.back_to_login')}
              </Link>
            </p>
          </form>
        )}
      </div>
    </main>
  )
}
